/**
 * Prelaunch gate helpers.
 *
 * When REACT_APP_PRELAUNCH=1, the app is in "Coming Soon" mode:
 *   - Signup / purchase / tip / subscribe flows are hidden and blocked.
 *   - Every route except the whitelist below redirects to `/` (ComingSoon page).
 *
 * BYPASS: Only unlocked by logging in with an admin account through the
 * hidden staff-login modal on the Coming Soon page. When the /api/auth/me
 * response returns `is_admin: true`, we flip a localStorage flag that
 * unlocks the full app for that browser. Non-admins never see anything
 * beyond ComingSoon — there is no URL or secret key that grants access.
 */

// The single knob that flips the whole app between prelaunch and live modes.
// Set to "1" to force prelaunch mode; anything else means the app is live.
const RAW_FLAG = (process.env.REACT_APP_PRELAUNCH || "").trim();

// LocalStorage flag set only after a successful admin login.
const UNLOCK_STORAGE_KEY = "mysticast_admin_unlock";
// Separate flag for the view-only demo (buyer preview) account. Kept
// distinct from the admin unlock so that revoking admin access via other
// flows never silently opens the gate for a demo session, and vice-versa.
const DEMO_UNLOCK_STORAGE_KEY = "mysticast_demo_unlock";

/** Whitelisted routes still viewable in prelaunch mode (business-info + legal). */
export const PRELAUNCH_ALLOWED_PATHS = [
  "/",
  "/about",
  "/terms",
  "/privacy",
  "/refunds",
  "/contact",
  "/cookies",
  "/safety",
  "/dmca",
  "/careers",
  "/community",
  // Dedicated buyer-preview sign-in page. Melissa shares this URL +
  // demo credentials with potential buyers so they can walk through the
  // full app without seeing the Coming Soon splash.
  "/preview",
];

/** True if the browser has been admin-unlocked OR demo-unlocked. Either
 * flag lets the user through the Coming Soon gate; both are set only after
 * a verified login against the backend. */
export function isAdminUnlocked() {
  try {
    return (
      localStorage.getItem(UNLOCK_STORAGE_KEY) === "1" ||
      localStorage.getItem(DEMO_UNLOCK_STORAGE_KEY) === "1"
    );
  } catch {
    return false;
  }
}

/** Flip the admin unlock ON — only call after verifying /auth/me returns
 * is_admin=true. */
export function grantAdminUnlock() {
  try {
    localStorage.setItem(UNLOCK_STORAGE_KEY, "1");
  } catch {
    // no-op
  }
}

/** Flip the demo unlock ON — only call after verifying /auth/me returns
 * is_demo=true. Stays separate from the admin unlock so clearing one does
 * not accidentally grant the other. */
export function grantDemoUnlock() {
  try {
    localStorage.setItem(DEMO_UNLOCK_STORAGE_KEY, "1");
  } catch {
    // no-op
  }
}

/** Revoke BOTH unlocks — used by logout. */
export function revokeAdminUnlock() {
  try {
    localStorage.removeItem(UNLOCK_STORAGE_KEY);
    localStorage.removeItem(DEMO_UNLOCK_STORAGE_KEY);
  } catch {
    // no-op
  }
}

/** True iff we should show the Coming Soon experience right now. */
export function isPrelaunch() {
  if (RAW_FLAG !== "1") return false;
  return !isAdminUnlocked();
}

export function isPathAllowedInPrelaunch(pathname) {
  if (PRELAUNCH_ALLOWED_PATHS.includes(pathname)) return true;
  // OBS browser sources need to work even before public launch —
  // streamers can preview their alert overlay while onboarding.
  if (pathname === "/alert-overlay") return true;
  return false;
}
